import { useState } from "react";
import {
  startRegistration,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

import {
  PASSKEY_ADDED,
  PASSKEY_ADD_FAILED,
  PASSKEY_ADD_STARTED,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useToast } from "hooks/useToast";
import { humanizeWebAuthnError } from "utils/humanizeWebAuthnError";
import { BackendError } from "types/utils/error";
import {
  useWebauthnRegistrationBeginMutation,
  useWebauthnRegistrationFinishMutation,
} from "store/apis/webauthn";

const DEFAULT_PASSKEY_NAME = "Passkey";

export const usePasskeyRegistration = (onAdded?: () => void) => {
  const [isStarting, setIsStarting] = useState(false);
  const [pendingCredential, setPendingCredential] =
    useState<RegistrationResponseJSON | null>(null);
  const { alertToast, successToast } = useToast();
  const { trackEvent } = useAnalytics();

  const [webauthnRegistrationBegin] = useWebauthnRegistrationBeginMutation();
  const [webauthnRegistrationFinish, { isLoading: isSaving }] =
    useWebauthnRegistrationFinishMutation();

  const startCeremony = async () => {
    setIsStarting(true);
    try {
      const options = await webauthnRegistrationBegin().unwrap();
      const credential = await startRegistration({ optionsJSON: options });
      setPendingCredential(credential);
    } catch (error) {
      trackEvent(PASSKEY_ADD_FAILED);
      const backendMessage = (error as BackendError)?.data?.error?.message;
      alertToast(
        { message: backendMessage || humanizeWebAuthnError(error) },
        error as object
      );
    } finally {
      setIsStarting(false);
    }
  };

  const finishCeremony = async (deviceName: string) => {
    if (!pendingCredential) return;
    try {
      await webauthnRegistrationFinish({
        credential: pendingCredential,
        device_name: deviceName,
      }).unwrap();
      trackEvent(PASSKEY_ADDED);
      successToast({ message: "Passkey added" });
      setPendingCredential(null);
      onAdded?.();
    } catch (error) {
      trackEvent(PASSKEY_ADD_FAILED);
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  const start = () => {
    trackEvent(PASSKEY_ADD_STARTED);
    void startCeremony();
  };

  const finishWithName = (deviceName: string) => {
    void finishCeremony(deviceName);
  };

  const finishWithDefaultName = () => {
    void finishCeremony(DEFAULT_PASSKEY_NAME);
  };

  return {
    isStarting,
    isSaving,
    isNamingCredential: !!pendingCredential,
    start,
    finishWithName,
    finishWithDefaultName,
  };
};
